import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  LinearProgress,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import {
  ApiParams,
  AppSnackbar,
  PageTemplate,
  PageTemplateCardOver,
  PageTemplateHeader,
  PageTemplateHeaderActions
} from '@sierralabs/react-material-ui';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import SearchBox from '../../common/SearchBox';
import ListMoreButtonMenu from '../../common/ListMoreButtonMenu';
import { ChangeOrderCreateDialog } from './ChangeOrderCreateDialog';
import { useGetChangeOrderList } from './ChangeOrder.hook';
import { ChangeOrder } from './ChangeOrder.interface';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import { useHistory } from 'react-router-dom';
import { formatDate, stripHtml } from '../../common/formatter';
import ChangeOrderListFilter, {
  ChangeOrderListFilterOptions
} from './components/ChangeOrderListFilter';
import environment from '../../environments';
import { TypeOfChangeOptions } from './cards/ChangeOrderDetailsCard';
import WrappedTableCell from '../../common/WrappedTableCell';

const ChangeOrderListPage: React.FC = () => {
  const [apiParams, setApiParams] = useState<ApiParams>({});
  const [filter = '', setFilter] = useQueryParam('filter', StringParam);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);
  const [showPrompt, setShowPrompt] = useState(false);
  const {
    data: changeOrderList,
    isLoading,
    error,
    getChangeOrders
  } = useGetChangeOrderList();
  const [includeDeleted = false, setIncludeDeleted] = useQueryParam(
    'deleted',
    withDefault(BooleanParam, false)
  );
  const history = useHistory();
  const usingCustomField: boolean | undefined =
    environment.changeOrderConfig?.useCustomFields;

  const getChangeOrdersWithParams = useCallback(() => {
    getChangeOrders({
      filter,
      page,
      limit,
      order,
      direction,
      includeDeleted,
      ...apiParams
    });
  }, [
    getChangeOrders,
    filter,
    page,
    limit,
    order,
    direction,
    includeDeleted,
    apiParams
  ]);

  useEffect(() => {
    getChangeOrdersWithParams();
  }, [getChangeOrdersWithParams]);

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  const getSortName = (columnName: string) => {
    switch (columnName) {
      case 'requestDate':
        return `changeOrder.${usingCustomField ? 'created' : 'requestDate'}`;
      case 'requestor':
        return `changeOrder.${usingCustomField ? 'createdBy' : 'requestor'}`;
      case 'impactOfChange':
        return `changeOrder.${
          usingCustomField ? 'typeOfChange' : 'impactOfChange'
        }`;
      default:
        return '';
    }
  };

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Change Orders</h2>
        <SearchBox value={filter} onSearch={setFilter}>
          <ChangeOrderListFilter
            onFilter={(options: ChangeOrderListFilterOptions) => {
              const { status } = options;
              const params: ApiParams = { ...apiParams, status };
              status.length === 0 && delete params.status;
              setApiParams(params);
            }}
          />
        </SearchBox>
        <PageTemplateHeaderActions>
          <Button
            variant='contained'
            color='primary'
            onClick={() => setShowPrompt(true)}
          >
            Add Change Order
          </Button>
          <ListMoreButtonMenu>
            <ListItem button onClick={() => setIncludeDeleted(!includeDeleted)}>
              <ListItemText
                primary={`${includeDeleted ? 'Hide' : 'Show'} Deleted`}
              />
            </ListItem>
          </ListMoreButtonMenu>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'changeOrder.id'}
                    direction={getSortDirection('changeOrder.id')}
                    onClick={() => handleSort('changeOrder.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'changeOrder.name'}
                    direction={getSortDirection('changeOrder.name')}
                    onClick={() => handleSort('changeOrder.name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'changeOrder.description'}
                    direction={getSortDirection('changeOrder.description')}
                    onClick={() => handleSort('changeOrder.description')}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === getSortName('requestDate')}
                    direction={getSortDirection(getSortName('requestDate'))}
                    onClick={() => handleSort(getSortName('requestDate'))}
                  >
                    Request Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === getSortName('requestor')}
                    direction={getSortDirection(getSortName('requestor'))}
                    onClick={() => handleSort(getSortName('requestor'))}
                  >
                    Requestor
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === getSortName('impactOfChange')}
                    direction={getSortDirection(getSortName('impactOfChange'))}
                    onClick={() => handleSort(getSortName('impactOfChange'))}
                  >
                    {usingCustomField ? 'Type of Change' : 'Impact of Change'}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {changeOrderList &&
                changeOrderList.data.map((changeOrder: ChangeOrder) => (
                  <TableRow
                    hover
                    key={changeOrder.id}
                    onClick={() =>
                      history.push(
                        `/portal/change-order/task/${changeOrder.taskId}`
                      )
                    }
                  >
                    <TableCell>{`CO-${changeOrder.id}`}</TableCell>
                    <TableCell>{changeOrder.name}</TableCell>
                    <WrappedTableCell>
                      <div className='truncate'>
                        {stripHtml(changeOrder.description || '')}
                      </div>
                    </WrappedTableCell>
                    <TableCell>
                      {formatDate(
                        usingCustomField
                          ? changeOrder.created
                          : changeOrder.requestDate,
                        true
                      )}
                    </TableCell>
                    <TableCell>
                      {usingCustomField && changeOrder.createdBy
                        ? `${changeOrder.createdBy.firstName} ${changeOrder.createdBy.lastName}`
                        : !usingCustomField && changeOrder.requestor
                        ? `${changeOrder.requestor.firstName} ${changeOrder.requestor.lastName}`
                        : ''}
                    </TableCell>
                    <TableCell>
                      {usingCustomField
                        ? changeOrder.typeOfChange
                            ?.map(
                              value =>
                                TypeOfChangeOptions.find(
                                  options => options.value === value
                                )?.label
                            )
                            .join(', ')
                        : changeOrder.impactOfChange}
                    </TableCell>
                    <TableCell>
                      <TaskStatusLabel task={changeOrder.task} />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={changeOrderList?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </PageTemplateCardOver>
      <AppSnackbar severity='error' open={Boolean(error)}>
        {error?.message}
      </AppSnackbar>
      {showPrompt && (
        <ChangeOrderCreateDialog
          open={showPrompt}
          onCancel={() => setShowPrompt(false)}
          onCreate={() => {
            setShowPrompt(false);
            getChangeOrdersWithParams();
          }}
        />
      )}
    </PageTemplate>
  );
};

export default ChangeOrderListPage;
